import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';

function RequestActionModal({ 
  isOpen, 
  onClose, 
  requestId, 
  requestTitle,
  actionType, 
  onApprove, 
  onReject, 
  onFulfill 
}) {
  const [pickupTime, setPickupTime] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (actionType === 'approve') {
        await onApprove(requestId, { pickup_time: pickupTime });
      } else if (actionType === 'reject') {
        await onReject(requestId, { notes });
      } else if (actionType === 'fulfill') {
        await onFulfill(requestId);
      }
    } catch (error) {
      console.error('Error in request action:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => !loading && onClose()}
      className="fixed z-10 inset-0 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
        
        <div className="relative bg-white rounded-lg max-w-md w-full mx-4 p-6 shadow-xl">
          <Dialog.Title className="text-lg font-medium text-gray-900">
            {actionType === 'approve' && 'Approve Request'}
            {actionType === 'reject' && 'Reject Request'}
            {actionType === 'fulfill' && 'Mark Request as Fulfilled'}
          </Dialog.Title>
          
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              {actionType === 'approve' && 'Please specify when the food can be picked up.'}
              {actionType === 'reject' && 'Please provide a reason for rejecting this request.'}
              {actionType === 'fulfill' && 'Are you sure you want to mark this request as fulfilled? This action confirms that the food has been picked up by the recipient.'}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-700">
                Request for: <span className="font-bold">{requestTitle}</span>
              </p>
            </div>
            
            {/* Pickup time input for approval */}
            {actionType === 'approve' && (
              <div>
                <label htmlFor="pickup_time" className="block text-sm font-medium text-gray-700">
                  Pickup Time
                </label>
                <input
                  type="datetime-local"
                  id="pickup_time"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  required
                />
              </div>
            )}
            
            {/* Notes input for rejection */}
            {actionType === 'reject' && (
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                  Reason for Rejection
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  required
                />
              </div>
            )}
            
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="submit"
                disabled={loading}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:col-start-2 sm:text-sm
                  ${actionType === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : ''}
                  ${actionType === 'reject' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : ''}
                  ${actionType === 'fulfill' ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' : ''}
                  focus:outline-none focus:ring-2 focus:ring-offset-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}
                `}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    {actionType === 'approve' && 'Approve'}
                    {actionType === 'reject' && 'Reject'}
                    {actionType === 'fulfill' && 'Mark Fulfilled'}
                  </>
                )}
              </button>
              <button
                type="button"
                disabled={loading}
                className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}

export default RequestActionModal;
